import { h } from 'preact'
import { useEffect, useCallback } from 'preact/hooks'
import { useAppState } from 'lib/appState'
import { useOrganizations } from './membershipAppStateHooks'

export function useOrganizationFeedSubscriptions(organizationApikey, componentName) {
  const organizationFeedSubscriptionsKey = `organization:${organizationApikey}:feedSubscriptions`
  const {
    takeAction,
    organizationFeedSubscriptionUids,
    organizationFeedSubscriptionsLoaded,
    errorLoadingOrganizationFeedSubscriptions,
    subscribingToOrganizationFeed,
    loadingOrganizationFeedSubscriptions,
    errorSubscribingToOrganizationFeed,
    unsubscribingFromOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
  } = useAppState(
    {
      [organizationFeedSubscriptionsKey]: 'organizationFeedSubscriptionUids',
      [`${organizationFeedSubscriptionsKey}:loaded`]: 'organizationFeedSubscriptionsLoaded',
      [`${organizationFeedSubscriptionsKey}:loading`]: 'loadingOrganizationFeedSubscriptions',
      [`${organizationFeedSubscriptionsKey}:loading:error`]: 'errorLoadingOrganizationFeedSubscriptions',
      [`feedSubscriptions:subscribing`]: 'subscribingToOrganizationFeed',
      [`feedSubscriptions:subscribing:error`]: 'errorSubscribingToOrganizationFeed',
      [`feedSubscriptions:unsubscribing`]: 'unsubscribingFromOrganizationFeed',
      [`feedSubscriptions:unsubscribing:error`]: 'errorUnsubscribingFromOrganizationFeed',
    },
    componentName,
  )

  const { organizationFeedSubscriptions } = useFeedSubscriptions(
    organizationFeedSubscriptionUids || [], componentName,
  )

  const needsLoading = (
    !errorLoadingOrganizationFeedSubscriptions &&
    !loadingOrganizationFeedSubscriptions &&
    !organizationFeedSubscriptionsLoaded
  )

  useEffect(
    () => {
      if (needsLoading){
        takeAction('feedSubscriptions.load', organizationApikey)
      }
    },
    [needsLoading, organizationApikey]
  )

  return {
    subscribeToOrganizationTopic: useCallback(
      (subscriberOrganizationApikey, topicUid, subscriberTopicUid) => {
        takeAction('feedSubscriptions.subscribe', {
          organizationApikey,
          subscriberOrganizationApikey,
          topicUid,
          subscriberTopicUid
        })
      },
      [organizationApikey],
    ),
    unsubscribeFromOrganizationTopic: useCallback(
      // the unsubscribe action destructures { organizationFeedSubscription }
      (organizationFeedSubscription) => {
        takeAction('feedSubscriptions.unsubscribe', { organizationFeedSubscription })
      },
      [],
    ),
    organizationFeedSubscriptionUids,
    organizationFeedSubscriptions,
    errorLoadingOrganizationFeedSubscriptions,
    subscribingToOrganizationFeed,
    errorSubscribingToOrganizationFeed,
    unsubscribingFromOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
    loadingOrganizationFeedSubscriptions,
  }
}

export function useOrganizationFeedSubscription(
  {
    organizationApikey,
    subscriberOrganizationApikey,
    topicUid,
    subscriberTopicUid,
  },
  componentName
) {
  const {
    takeAction,
    subscribeeFeedSubscriptionUids = new Set(),
    subscriberFeedSubscriptionUids = new Set(),
    subscribingToOrganizationFeed,
    errorSubscribingToOrganizationFeed,
    unsubscribingFromOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
  } = useAppState(
    {
      [`organization:${organizationApikey}:feedSubscriptions`]: 'subscribeeFeedSubscriptionUids',
      [`organization:${subscriberOrganizationApikey}:feedSubscriptions`]: 'subscriberFeedSubscriptionUids',
      [`feedSubscriptions:subscribing`]: 'subscribingToOrganizationFeed',
      [`feedSubscriptions:subscribing:error`]: 'errorSubscribingToOrganizationFeed',
      [`feedSubscriptions:unsubscribing`]: 'unsubscribingFromOrganizationFeed',
      [`feedSubscriptions:unsubscribing:error`]: 'errorUnsubscribingFromOrganizationFeed',
    },
    componentName,
  )

  const { organizationFeedSubscriptions } = useFeedSubscriptions(
    [
      ...subscribeeFeedSubscriptionUids,
      ...subscriberFeedSubscriptionUids,
    ],
    componentName,
  )

  // NB: this is an ARRAY (all subscriptions where subscriberOrganizationApikey is
  // the subscriber). Callers that need a yes/no should use `isSubscribed`, not
  // the truthiness of this array (an empty array is still truthy).
  const organizationFeedSubscription = organizationFeedSubscriptions.filter(s =>
    s.subscriberOrganizationApikey === subscriberOrganizationApikey
  )

  // the single subscription matching this exact source→destination channel pair
  // (only meaningful when all four keys are supplied)
  const matchingSubscription = organizationFeedSubscriptions.find(s =>
    s.organizationApikey === organizationApikey &&
    s.subscriberOrganizationApikey === subscriberOrganizationApikey &&
    s.topicUid === topicUid &&
    s.subscriberTopicUid === subscriberTopicUid
  )
  const isSubscribed = !!matchingSubscription

  const subscribing = (
    subscribingToOrganizationFeed &&
    subscribingToOrganizationFeed.organizationApikey === organizationApikey &&
    subscribingToOrganizationFeed.subscriberOrganizationApikey === subscriberOrganizationApikey
  )

  const unsubscribing = !!(
    unsubscribingFromOrganizationFeed &&
    unsubscribingFromOrganizationFeed.subscriberOrganizationApikey === subscriberOrganizationApikey
  )

  const isTopicHasSubscriptions = useCallback(
    ({ topicUid, subscriberTopicUid } = {}) =>
      organizationFeedSubscriptions.some(
        sub => sub.topicUid === topicUid || sub.subscriberTopicUid === subscriberTopicUid
      ),
    [organizationFeedSubscriptions]
  )


  return {
    subscribe: useCallback(
      (args = {}) => {
        takeAction('feedSubscriptions.subscribe', {
          organizationApikey: args.organizationApikey || organizationApikey,
          subscriberOrganizationApikey,
          topicUid: args.topicUid || topicUid,
          subscriberTopicUid: args.subscriberTopicUid || subscriberTopicUid,
        })
      },
      [organizationApikey, subscriberOrganizationApikey, subscriberTopicUid, topicUid]
    ),
    unsubscribe: useCallback(
      // pass an explicit subscription, or fall back to the matching one so the
      // payload always carries subscriberOrganizationApikey (routes to the b-server)
      (subscription) => {
        takeAction('feedSubscriptions.unsubscribe', {
          organizationFeedSubscription: subscription || matchingSubscription,
        })
      },
      [matchingSubscription]
    ),
    organizationFeedSubscription,
    matchingSubscription,
    isSubscribed,
    subscribing,
    errorSubscribing: errorSubscribingToOrganizationFeed,
    unsubscribing,
    errorUnsubscribing: errorUnsubscribingFromOrganizationFeed,
    isTopicHasSubscriptions,
  }
}

export function useMyOrganizationFeedSubscriptions(componentName) {
  const {
    takeAction,
    subscribingToOrganizationFeed,
    errorSubscribingToOrganizationFeed,
    unsubscribingFromOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
    myOrganizationFeedSubscriptionUids,
    myOrganizationFeedSubscriptionUidsLoaded,
    myOrganizationFeedSubscriptionUidsLoading,
    myOrganizationFeedSubscriptionUidsLoadingError,
  } = useAppState(
    {
      'feedSubscriptions:subscribing': 'subscribingToOrganizationFeed',
      'feedSubscriptions:subscribing:error': 'errorSubscribingToOrganizationFeed',
      'feedSubscriptions:unsubscribing': 'unsubscribingFromOrganizationFeed',
      'feedSubscriptions:unsubscribing:error': 'errorUnsubscribingFromOrganizationFeed',
      'myOrganizationFeedSubscriptionUids': 'myOrganizationFeedSubscriptionUids',
      'myOrganizationFeedSubscriptionUids:loaded': 'myOrganizationFeedSubscriptionUidsLoaded',
      'myOrganizationFeedSubscriptionUids:loading': 'myOrganizationFeedSubscriptionUidsLoading',
      'myOrganizationFeedSubscriptionUids:loading:error': 'myOrganizationFeedSubscriptionUidsLoadingError',
    },
    componentName
  )

  let {
    organizationFeedSubscriptions: myOrganizationFeedSubscriptions
  } = useFeedSubscriptions(
    myOrganizationFeedSubscriptionUids || [],
    componentName,
  )
  if (!myOrganizationFeedSubscriptionUids) myOrganizationFeedSubscriptions = undefined

  useEffect(
    () => {
      if (!myOrganizationFeedSubscriptionUidsLoaded)
        takeAction('feedSubscriptions.loadMine')
    },
    [myOrganizationFeedSubscriptionUidsLoaded]
  )

  return {
    subscribeToOrganizationFeed: useCallback(
      organizationApikey => {
        takeAction('feedSubscriptions.subscribe', { organizationApikey })
      },
      [],
    ),
    unsubscribeFromOrganizationFeed: useCallback(
      organizationFeedSubscription => {
        takeAction('feedSubscriptions.unsubscribe', { organizationFeedSubscription })
      },
      [],
    ),
    subscribingToOrganizationFeed,
    errorSubscribingToOrganizationFeed,
    unsubscribingFromOrganizationFeed,
    errorUnsubscribingFromOrganizationFeed,
    myOrganizationFeedSubscriptionUids,
    myOrganizationFeedSubscriptionUidsLoaded,
    myOrganizationFeedSubscriptionUidsLoading,
    myOrganizationFeedSubscriptionUidsLoadingError,
    myOrganizationFeedSubscriptions,
    myOrganizationFeedSubscriptionLoaded: myOrganizationFeedSubscriptionUidsLoaded,
    myOrganizationFeedSubscriptionLoading: myOrganizationFeedSubscriptionUidsLoading,
    myOrganizationFeedSubscriptionLoadingError: myOrganizationFeedSubscriptionUidsLoadingError,
  }
}

function useFeedSubscriptions(organizationFeedSubscriptionUids, componentName) {
  const appState = useAppState(
    Array.from(organizationFeedSubscriptionUids).map(uid => `organizationFeedSubscription:${uid}`),
    componentName,
  )

  const organizationFeedSubscriptions = []
  organizationFeedSubscriptionUids.forEach(uid => {
    const organizationFeedSubscription = appState[`organizationFeedSubscription:${uid}`]
    if (!organizationFeedSubscription) {
      console.warn(`organizationFeedSubscription uid="${uid}" not found in appState`)
      return
    }
    organizationFeedSubscriptions.push(organizationFeedSubscription)
  })
  organizationFeedSubscriptions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  return { organizationFeedSubscriptions }
}

export function useTopicsSubscriptionsDetails(organizationApikey, topicUid, componentName) {
  const topicsSubscribedToMyTopicKey = `${organizationApikey}:topicsSubscribedToMyTopic:${topicUid}`
  const myTopicSubscriptionsKey = `${organizationApikey}:myTopicsSubscriptions:${topicUid}`
  const topicsSubscribedToMyTopicErrorKey = `${organizationApikey}:topicsSubscribedToMyTopic:${topicUid}:error`
  const myTopicsSubscriptionsErrorKey = `${organizationApikey}:myTopicsSubscriptions:${topicUid}:error`

  const {
    takeAction,
    topicsSubscribedToMyTopic = [],
    myTopicSubscriptions = [],
    loadingTopicsSubscriptionsDetails = false,
    errorLoadingTopicsSubscriptionsDetails = null,
    loadedTopicsSubscriptionsDetails = false,
  } = useAppState(
    {
      [topicsSubscribedToMyTopicKey]: 'topicsSubscribedToMyTopic',
      [myTopicSubscriptionsKey]: 'myTopicSubscriptions',
      [`${organizationApikey}:loadingTopicsSubscriptionsDetails`]: 'loadingTopicsSubscriptionsDetails',
      [topicsSubscribedToMyTopicErrorKey]: 'errorLoadingTopicsSubscriptionsDetails',
      [myTopicsSubscriptionsErrorKey]: 'errorLoadingTopicsSubscriptionsDetails',
      [`${organizationApikey}:loadedTopicsSubscriptionsDetails`]: 'loadedTopicsSubscriptionsDetails',
    },
    componentName
  )

  useEffect(() => {
    takeAction('feedSubscriptions.loadTopicsSubscriptionsDetails', {
      organizationApikey,
      topicUid,
    })
  }, [organizationApikey, topicUid, takeAction])

  const organizationApikeys = [
    ...new Set([
      ...myTopicSubscriptions.map(sub => sub.organizationapikey),
      ...topicsSubscribedToMyTopic.map(sub => sub.organizationapikey)
    ])
  ]

  const { organizations } = useOrganizations(organizationApikeys, componentName)

  const attachOrganizationsToTopics = (topics, organizations) => {
    return topics.map(topic => ({
      ...topic,
      organization: organizations.find(org => org.apikey === topic.organizationapikey) || null,
    }))
  }

  const topicsWithOrganizations = {
    topicsSubscribedToMyTopic: attachOrganizationsToTopics(topicsSubscribedToMyTopic, organizations),
    myTopicSubscriptions: attachOrganizationsToTopics(myTopicSubscriptions, organizations),
  }

  return {
    ...topicsWithOrganizations,
    isLoading: loadingTopicsSubscriptionsDetails,
    error: errorLoadingTopicsSubscriptionsDetails,
    isLoaded: loadedTopicsSubscriptionsDetails,
  }
}
